import {
  Button,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
  useTheme,
  Box,
  IconButton,
  TextField,
  Tooltip,
  Container,
  Paper,
} from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import { useFormik } from "formik";
import TableRow from "@mui/material/TableRow";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  bloqcibeApi,
  useAssignRoleMutation,
  useDeleteUserRoleMutation,
  useGetSponsorLevelUsersQuery,
} from "../../../store/slices/apiSlice";
import AddMember from "./AddMember";
import { UserRole } from "../../../util/util";
import MamberDocumentUpload from "../../../components/common/MemberDocumentUpload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  CustomTableHead,
  CustomTableHeadCell,
} from "../../../components/@extended/CustomTable";
import CustomButton from "../../../components/@extended/CustomButton";
import TableCellLabel from "../../../components/common/TableCellLabel";
import { convertToTitleCase, getUserRole } from "../../util";
import { selectCurrentUser } from "../../../store/slices/authSlice";
import { openMessage } from "../../../store/slices/showMessageSlice";
import ConfirmationDialog from "../../../components/common/ConfirmationDialog";

const ManageMembers = ({
  siteId,
  trialId,
  onBackClick,
  siteName,
  handleClose,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openAddMember, setOpenAddMember] = useState(false);
  const [openUploadDoc, setOpenUploadDoc] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedDocUser, setSelectedDocUser] = useState("");
  const [assignRole, {isLoading}] = useAssignRoleMutation();
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  const [getSiteMembers, { data: siteUsersData }] =
    bloqcibeApi.endpoints.getSiteMembers.useLazyQuery();
  const [getTrialSiteMembers, { data: trialSiteUsersData }] =
    bloqcibeApi.endpoints.getTrialSiteMembers.useLazyQuery();
  // Fetch all sponsor-level users (Data Managers and Site Monitors)
  const { data: sponsorLevelUsersData } = useGetSponsorLevelUsersQuery({ sponsorId });
  const [deleteUserRole] = useDeleteUserRoleMutation();
  const user = useSelector(selectCurrentUser);
  const userData = useSelector((state) => state.auth.userData);
  const userRole = getUserRole(user, trialId, null, userData);
  const [selectedUserType, setSelectedUserType] = useState('site_staff'); 
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: (
      <>
        <span>Are you sure you want to delete this user from the Trial?</span>
        <br />
        <span>
          This action cannot be undone. Please confirm whether you want to
          proceed with the deletion.
        </span>
      </>
    ),
    buttonLabel: "Confirm",
  });
  const [deleteUser,setDeleteUser]=useState()

  // Memoized user options for dropdown
  const userOptions = useMemo(() => {
    const options = [];

    if (siteUsersData) {
      const filteredSiteUsers = siteUsersData.filter((_siteUser) => {
        // Filter out already assigned users
        if (!trialSiteUsersData) return true;
        const _rec = trialSiteUsersData.find((_trialData) => _trialData.userId == _siteUser.id);
        return !_rec;
      });

      if (filteredSiteUsers.length > 0) {
        options.push({
          optionType: 'group',
          label: 'Site Staff',
        });
      }
      filteredSiteUsers.forEach((_user) => {
        options.push({
          id: _user.id,
          name: `${_user.firstName} ${_user.lastName}`,
          contactNumber: _user.contactNumber,
          type: 'site_staff',
          optionType: 'option',
        });
      });
    }

    // Separate data managers and site monitors from sponsor-level users
    const dataManagers =
      sponsorLevelUsersData?.filter((user) => {
        const isDataManager = user.userType === 'data_manager' || user.user?.userMetaInfo?.userType === 'data_manager';
        if (!trialSiteUsersData) return true;
        const _rec = trialSiteUsersData.find((_trialData) => _trialData.userId == user.user?.id);
        return !_rec && isDataManager;
      }) || [];

    const siteMonitors =
      sponsorLevelUsersData?.filter((user) => {
        const isSiteMonitor = user.userType === 'site_monitor' || user.user?.userMetaInfo?.userType === 'site_monitor';
        if (!trialSiteUsersData) return true;
        const _rec = trialSiteUsersData.find((_trialData) => _trialData.userId == user.user?.id);
        return !_rec && isSiteMonitor;
      }) || [];

    if (siteMonitors.length > 0) {
      options.push({
        optionType: 'group',
        label: 'Site Monitors',
      });

      siteMonitors.forEach((_sm) => {
        options.push({
          id: _sm.user?.id,
          name: `${_sm.user?.firstName} ${_sm.user?.lastName}`,
          contactNumber: _sm.user?.contactNumber,
          type: 'site_monitor',
          optionType: 'option',
        });
      });
    }
    // Add Data Managers group
    if (dataManagers.length > 0) {
      options.push({
        optionType: 'group',
        label: 'Data Managers',
      });

      // Add Site Monitors group

      dataManagers.forEach((_dm) => {
        options.push({
          id: _dm.user?.id,
          name: `${_dm.user?.firstName} ${_dm.user?.lastName}`,
          contactNumber: _dm.user?.contactNumber,
          type: 'data_manager',
          optionType: 'option',
        });
      });
    }

    return options;
  }, [siteUsersData, sponsorLevelUsersData, trialSiteUsersData]);

  useEffect(() => {
    (async () => {
      await getSiteMembers({ sponsorId, siteId });
      await getTrialSiteMembers({ sponsorId, siteId, trialId });
    })();
  }, [dispatch]);
  useEffect(() => {
    (async () => {
      if (trialSiteUsersData) {
        setRows(trialSiteUsersData);
      }
    })();
  }, [trialSiteUsersData]);

  const memberSchema = Yup.object().shape({
    userId: Yup.string().required("Name is required"),
    //contact: Yup.string().required("Contact info is required"),
    role: Yup.string().required("Role is required"),
    roleCode: Yup.string(),
  });
  const formik = useFormik({
    initialValues: {
      userId: '',
      contact: '',
      role: '',
      roleCode: '',
    },
    validationSchema: memberSchema,
    onSubmit: async (values) => {
      try {
        const result = await assignRole({
          data: { ...values, trialId, siteId },
          sponsorId: sponsorId,
        }).unwrap();
        if (result) {
          dispatch(
            openMessage({
              messageSeverity: 'success',
              message: 'User role assigned successfully',
            })
          );
        } else {
          dispatch(
            openMessage({
              messageSeverity: 'error',
              message: 'Failed to assign user role',
            })
          );
        }
        formik.resetForm();
        await getTrialSiteMembers({ sponsorId, siteId, trialId });
      } catch (error) {
        dispatch(
          openMessage({
            messageSeverity: 'error',
            message: error?.data?.message || 'Failed to assign user role',
          })
        );
      }
    },
  });

  const columns = [
    { Header: "Name", align: "left" },
    { Header: "Contact", align: "left" },
    { Header: "Role", align: "left" },
    { Header: "Employee Code", align: "left" },
    { Header: "Action", align: "left" },
  ];

  const addMember = () => {
    setOpenAddMember(true);
  };

  const onClose = () => {
    setOpenAddMember(false);
  };

  const onCloseUpload = () => {
    setOpenUploadDoc(false);
  };

  const onAddMember = async () => {
    await getSiteMembers({ siteId, sponsorId });
    setOpenAddMember(false);
  };

  const onMemberChange = (e) => {
    const selectedValue = e.target.value;
    
    // First, handle the formik field change for userId
    formik.setFieldValue("userId", selectedValue);
    
    // Find the selected user from the memoized options
    let selectedUser = userOptions.find(option => option.id == selectedValue);
    let userType = selectedUser?.type;

    if (selectedUser) {
      formik.setFieldValue("contact", selectedUser.contactNumber);
      
      // If data manager or site monitor selected, set role automatically and disable role dropdown
      if (userType === 'data_manager') {
        setSelectedUserType('data_manager');
        formik.setFieldValue("role", "data_manager");
      } else if (userType === 'site_monitor') {
        setSelectedUserType('site_monitor');
        formik.setFieldValue("role", "site_monitor");
      } else {
        setSelectedUserType('site_staff');
        // Clear role if switching from data_manager/site_monitor to site_staff
        if (formik.values.role === 'data_manager' || formik.values.role === 'site_monitor') {
          formik.setFieldValue("role", "");
        }
      }
    } else {
      // Reset if no user selected
      setSelectedUserType('site_staff');
      formik.setFieldValue("contact", "");
      formik.setFieldValue("role", "");
    }
  };

  const deleteMember = async (user) => {
    const matchingUsers = rows.filter(row => row.role === user.role);
    if((user.role === 'site_admin' || user.role === 'PI') && matchingUsers.length===1){
      dispatch(
        openMessage({
          messageSeverity: "warning",
          message: `Cannot delete this member, atleast one ${user.role==='site_admin'?'Site Admin':user.role} must remain.`,
        })
      );
      return
    }
    if (user.role !== userRole) {
      setDeleteUser(user);
      setConfirmDialog({
        ...confirmDialog,
        open: true,
        message: (
          <>
            <span>
              Are you sure you want to delete{" "}
              {`${user.user.firstName} ${user.user.lastName}`} from the Study?
            </span>
            <br />
            <span>
              This action cannot be undone. Please confirm whether you want to
              proceed with the deletion.
            </span>
          </>
        ),
      });
    } else {
      dispatch(
        openMessage({
          messageSeverity: "warning",
          message: "Delete is not permitted for this user!",
        })
      );
    }
  };

  const uploadDoc = (userId) => {
    setSelectedDocUser(userId);
    setOpenUploadDoc(true);
  };

  const handleDeleteMember = async () => {
    if (deleteUser) {
     const result = await deleteUserRole({
        user_role_id: deleteUser?.id,
      });
      if(result?.data){
        dispatch(
          openMessage({
            messageSeverity: "success",
            message: "User Roledeleted successfully",
          })
        );
      } else {
        dispatch(
          openMessage({
            messageSeverity: "error",
            message: "Failed to delete user role",
          })
        );
      }
      setConfirmDialog({
        ...confirmDialog,
        open: false,
      });
      await getTrialSiteMembers({ sponsorId, siteId, trialId });
    }
  }

  const handleConfirmDialogClose = () => {
    setConfirmDialog({
      ...confirmDialog,
      open: false,
    });
  };
  return (
    <Box>
      <Container fixed>
        {onBackClick && (
          <Button component="label" variant="outlined" onClick={(e) => onBackClick()} startIcon={<ArrowBackIcon />}>
            Back
          </Button>
        )}
        <Card sx={{ height: '80vh', marginTop: 2 }}>
          <CardHeader
            title={`Study Team Details (${siteName})`}
            titleTypographyProps={{ variant: 'h6' }}
            action={
              <>
                {handleClose && (
                  <IconButton color="inherit" onClick={handleClose} aria-label="close">
                    <CloseIcon />
                  </IconButton>
                )}
              </>
            }
          />
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                columnGap: 4,
                alignItems: 'center',
                marginBottom: 3,
              }}
            >
              <Box sx={{ display: 'flex', width: '25%' }}>
                <Select
                  name={`userId`}
                  value={formik.values.userId}
                  onChange={onMemberChange}
                  fullWidth={true}
                  sx={{ width: '90%' }}
                  placeholder="Select Team Member"
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  error={formik.touched.userId && Boolean(formik.errors.userId)}
                  input={<OutlinedInput size="small" />}
                >
                  <MenuItem disabled value="">
                    <em style={{ color: '#aaa', fontStyle: 'normal' }}>Select Team Member</em>
                  </MenuItem>

                  {/* Render grouped options from memoized array */}
                  {userOptions.map((option, index) => {
                    if (option.optionType === 'group') {
                      return (
                        <MenuItem key={`group-${option.label}`} disabled>
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            // color={group.type === 'group' && group.label === 'Site Staff' ? 'primary' : 'secondary'}
                          >
                            {option.label}
                          </Typography>
                        </MenuItem>
                      );
                    } else {
                      return (
                        <MenuItem key={option.id} value={option.id} sx={{ pl: 4 }}>
                          <em>{option.name}</em>
                        </MenuItem>
                      );
                    }
                  })}
                </Select>
                <IconButton aria-label="Add" onClick={addMember}>
                  <AddCircleOutlineIcon />
                </IconButton>
              </Box>
              <TextField
                label="Contact Information"
                onChange={formik.handleChange}
                name="contact"
                value={formik.values.contact}
                size="small"
                disabled
                sx={{ width: '25%' }}
                variant="outlined"
                // helperText={formik.touched.contact && formik.errors.contact}
                // error={formik.touched.contact && Boolean(formik.errors.contact)}
              />
              <Select
                name={`role`}
                value={formik.values.role}
                onChange={formik.handleChange}
                placeholder="Select Role"
                sx={{ width: '25%' }}
                displayEmpty
                disabled={selectedUserType === 'data_manager' || selectedUserType === 'site_monitor'} // Disable if data manager or site monitor selected
                inputProps={{ 'aria-label': 'Without label' }}
                error={formik.touched.role && Boolean(formik.errors.role)}
                input={<OutlinedInput size="small" />}
              >
                <MenuItem disabled value="">
                  <em>Select Role</em>
                </MenuItem>
                {selectedUserType === 'data_manager' ? (
                  <MenuItem value="data_manager">Data Manager</MenuItem>
                ) : selectedUserType === 'site_monitor' ? (
                  <MenuItem value="site_monitor">Site Monitor</MenuItem>
                ) : (
                  UserRole.map((option) => {
                    return (
                      <MenuItem key={option.value} value={option.value}>
                        {option.name}
                      </MenuItem>
                    );
                  })
                )}
              </Select>
              <TextField
                label="Employee Code"
                onChange={formik.handleChange}
                name="roleCode"
                value={formik.values.roleCode}
                size="small"
                sx={{ width: '25%' }}
                variant="outlined"
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <CustomButton
                component="label"
                size="small"
                variant="contained"
                onClick={(e) => formik.handleSubmit()}
                disabled={isLoading}
              >
                Add Member
              </CustomButton>
            </Box>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 'medium' }}>
              Member Details
            </Typography>
            <Paper sx={{ width: '100%', marginTop: 3 }}>
              <Box
                sx={{
                  width: '100%',
                  height: 3,
                  backgroundColor: theme.palette.primary.light,
                }}
              />
              <TableContainer sx={{ maxHeight: '45vh' }}>
                <Table stickyHeader aria-label="sticky table">
                  <CustomTableHead>
                    <TableRow>
                      {columns.map((col) => (
                        <CustomTableHeadCell key={col.Header} align={col.align}>
                          {col.Header}
                        </CustomTableHeadCell>
                      ))}
                    </TableRow>
                  </CustomTableHead>
                  <TableBody sx={{ overflow: 'scroll' }}>
                    {rows && rows.length > 0 ? (
                      rows.map((row) => {
                        return (
                          <TableRow
                            key={row.id}
                            sx={{
                              '&:last-child td, &:last-child th': { border: 0 },
                            }}
                          >
                            <TableCell>
                              <TableCellLabel label={`${row?.user.firstName} ${row?.user.lastName}`} />
                            </TableCell>
                            <TableCell>
                              <TableCellLabel label={row?.user.contactNumber} />
                            </TableCell>
                            <TableCell>
                              <TableCellLabel label={convertToTitleCase(row?.role)} />
                            </TableCell>
                            <TableCell>
                              <TableCellLabel label={convertToTitleCase(row?.roleCode) || '-'} />
                            </TableCell>
                            <TableCell align="left">
                              <Tooltip title={'Upload'}>
                                <IconButton onClick={() => uploadDoc(row?.user)} color="primary">
                                  <CloudUploadIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={'Delete'}>
                                <IconButton onClick={() => deleteMember(row)} color={theme.palette.grey[500]}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="subtitle1" color="initial">
                            No Members Found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </CardContent>
        </Card>
      </Container>
      <AddMember open={openAddMember} handleClose={onClose} onAddMember={onAddMember} siteId={siteId} />
      <MamberDocumentUpload
        open={openUploadDoc}
        handleClose={onCloseUpload}
        trialId={trialId}
        siteId={siteId}
        memberId={selectedDocUser.id}
        memberName={`${selectedDocUser.firstName} ${selectedDocUser.lastName}`}
      />
      <ConfirmationDialog
        open={confirmDialog.open}
        buttonLabel={confirmDialog.buttonLabel}
        message={confirmDialog.message}
        handleClose={handleConfirmDialogClose}
        handleConfirm={handleDeleteMember}
      />
    </Box>
  );
};

export default ManageMembers;